// src/features/dashboard/dashboardSlice.ts
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
// import { dashboardFetchThunk } from "@core/api/dashboardApi"
import { getVisit, visitFetchThunk, type VisitElementsResponse } from "@core/api/visitApi"
import { VisitListSerializer } from "@/types/visit"
import { resetToToday, setSelectedDate } from "@/features/selectedDate/selectedDateSlice"
import { applyPatientListUpdates } from "./patientListUpdates"

// Dashboard slice에서 사용할 상태 타입 정의
export interface DashboardState {
  patients: VisitListSerializer[]
  loading: boolean
  error: string | null
  currentRequestId: string | null
  currentVisitDate: string | null
  generation: number
  baseValid: boolean
  singleRequests: Record<string, { requestId: string; generation: number }>
  dirtyVisitUuids: string[]
  recoveryNeeded: boolean
  fullRefreshQueued: boolean
  fullRequestGeneration: number
  authBlocked: boolean
}

const initialState: DashboardState = {
  patients: [],
  loading: false,
  error: null,
  currentRequestId: null,
  currentVisitDate: null,
  generation: 0,
  baseValid: false,
  singleRequests: {},
  dirtyVisitUuids: [],
  recoveryNeeded: false,
  fullRefreshQueued: false,
  fullRequestGeneration: 0,
  authBlocked: false,
}

const visitSlice = (name: string) => {
  const fetchList = visitFetchThunk(name)
  const fetchVisit = createAsyncThunk<
    VisitListSerializer,
    { visitUuid: string; visitDate: string; generation: number },
    { rejectValue: { status?: number; message: string } }
  >(
    `visit/${name}/single`,
    async ({ visitUuid }, { rejectWithValue }) => {
      try {
        return await getVisit(visitUuid)
      } catch (error: any) {
        return rejectWithValue({ status: error?.response?.status, message: error?.message || "방문 조회 실패" })
      }
    },
    {
      condition: ({ visitUuid, visitDate, generation }, { getState }) => {
        const state = (getState() as Record<string, DashboardState>)[`${name}Visit`]
        return Boolean(
          state?.baseValid &&
          !state.loading &&
          state.currentVisitDate === visitDate &&
          state.generation === generation &&
          !state.singleRequests[visitUuid]
        )
      },
    }
  )

  const invalidate = (state: DashboardState) => {
    state.generation += 1
    state.baseValid = false
    state.currentRequestId = null
    state.loading = false
    state.singleRequests = {}
    state.dirtyVisitUuids = []
    state.recoveryNeeded = false
    state.fullRefreshQueued = false
    state.authBlocked = false
  }

  const slice = createSlice({
    name: `visit/${name}`,
    initialState,
    reducers: {
      invalidate,
      applyElements: (state, action: PayloadAction<VisitElementsResponse & { generation: number }>) => {
        const { generation, visitDate, elements, order } = action.payload
        if (state.generation !== generation || !state.baseValid || state.loading || state.currentVisitDate !== visitDate) return
        const rows = new Map(state.patients.map(visit => [visit.visitUuid, visit]))
        for (const element of elements) {
          rows.delete(element.visitUuid)
          if (element.visit?.visitDate === visitDate && element.visit.visitStatus === name) rows.set(element.visitUuid, element.visit)
        }
        const ids = order[name as keyof typeof order]
        if (ids.some(uuid => !rows.has(uuid))) {
          state.baseValid = false
          state.recoveryNeeded = true
          return
        }
        state.patients = ids.map(uuid => rows.get(uuid)!)
        state.singleRequests = {}
        state.dirtyVisitUuids = []
        state.generation += 1
      },
      blockElementUpdates: state => {
        state.authBlocked = true
        state.baseValid = false
        state.recoveryNeeded = false
      },
      requestRecovery: state => {
        if (!state.fullRefreshQueued) {
          state.generation += 1
          state.baseValid = false
          state.dirtyVisitUuids = [...new Set([...state.dirtyVisitUuids, ...Object.keys(state.singleRequests)])]
          state.singleRequests = {}
        }
        state.fullRefreshQueued = true
      },
      queueVisit: (state, action: PayloadAction<string>) => {
        if (!state.dirtyVisitUuids.includes(action.payload)) state.dirtyVisitUuids.push(action.payload)
      },
      clearQueuedVisit: (state, action: PayloadAction<string>) => {
        state.dirtyVisitUuids = state.dirtyVisitUuids.filter(uuid => uuid !== action.payload)
      },
    },
    extraReducers: builder => {
      builder
        .addCase(applyPatientListUpdates, (state, action) => {
          const rows = action.payload.visits?.[name as "waiting" | "treatment" | "complete"]
          if (!rows) return
          state.patients = rows
          state.currentVisitDate = action.payload.date
          state.baseValid = true
          state.loading = false
          state.error = null
          state.authBlocked = false
          state.currentRequestId = null
          state.singleRequests = {}
          state.dirtyVisitUuids = []
          state.recoveryNeeded = false
          state.fullRefreshQueued = false
          state.generation += 1
        })
        .addCase(fetchList.pending, (state, action) => {
          state.dirtyVisitUuids = [...new Set([...state.dirtyVisitUuids, ...Object.keys(state.singleRequests)])]
          if (!state.fullRefreshQueued) state.generation += 1
          state.fullRefreshQueued = false
          state.fullRequestGeneration = state.generation
          state.baseValid = false
          state.singleRequests = {}
          state.recoveryNeeded = false
          state.authBlocked = false
          state.currentVisitDate = action.meta.arg || null
          state.loading = true
          state.error = null
          state.currentRequestId = action.meta.requestId
        })
        .addCase(fetchList.fulfilled, (state, action) => {
          if (state.currentRequestId !== action.meta.requestId) {
            return
          }
          state.loading = false
          state.currentRequestId = null
          if (state.fullRequestGeneration !== state.generation) return
          state.patients = action.payload
          state.currentVisitDate = action.meta.arg || null
          state.currentRequestId = null
          state.baseValid = true
        })
        .addCase(fetchList.rejected, (state, action) => {
          if (state.currentRequestId !== action.meta.requestId) {
            return
          }
          state.loading = false
          state.currentRequestId = null
          if (state.fullRequestGeneration !== state.generation) return
          state.error = (action.payload as string) || action.error.message || "알 수 없는 오류 발생"
          state.currentRequestId = null
          state.baseValid = false
        })
        .addCase(fetchVisit.pending, (state, action) => {
          state.singleRequests[action.meta.arg.visitUuid] = { requestId: action.meta.requestId, generation: action.meta.arg.generation }
          state.dirtyVisitUuids = state.dirtyVisitUuids.filter(uuid => uuid !== action.meta.arg.visitUuid)
        })
        .addCase(fetchVisit.fulfilled, (state, action) => {
          const { visitUuid, visitDate, generation } = action.meta.arg
          const pending = state.singleRequests[visitUuid]
          if (pending?.requestId !== action.meta.requestId || pending.generation !== generation) return
          delete state.singleRequests[visitUuid]
          if (!state.baseValid || state.loading || state.generation !== generation || state.currentVisitDate !== visitDate) return
          const index = state.patients.findIndex(visit => visit.visitUuid === visitUuid)
          const existing = state.patients[index]
          const updated = action.payload
          if (
            !existing ||
            updated.visitUuid !== visitUuid ||
            updated.visitStatus !== name ||
            updated.visitDate !== visitDate ||
            existing.visitStatus !== updated.visitStatus ||
            existing.visitDate !== updated.visitDate ||
            existing.visitStatusUpdatedDt !== updated.visitStatusUpdatedDt
          ) {
            state.baseValid = false
            state.recoveryNeeded = true
            return
          }
          state.patients[index] = updated
        })
        .addCase(fetchVisit.rejected, (state, action) => {
          const { visitUuid, generation } = action.meta.arg
          const pending = state.singleRequests[visitUuid]
          if (pending?.requestId !== action.meta.requestId || state.generation !== generation) return
          delete state.singleRequests[visitUuid]
          state.baseValid = false
          state.error = action.payload?.message || action.error.message || "방문 조회 실패"
          state.recoveryNeeded = action.payload?.status !== 401 && action.payload?.status !== 403
          state.authBlocked = !state.recoveryNeeded
        })
        .addCase(setSelectedDate, (state, action) => {
          if (state.currentVisitDate !== action.payload) invalidate(state)
        })
        .addCase(resetToToday, state => {
          invalidate(state)
        })
    },
  })

  return {
    reducer: slice.reducer,
    fetchList, // 이미 visitFetchThunk(name)에서 생성된 thunk
    fetchVisit,
    actions: slice.actions,
  }
}

// 개별 slice 생성 예시
export const waitingVisit = visitSlice("waiting")
export const treatmentVisit = visitSlice("treatment")
export const completeVisit = visitSlice("complete")
